// [S-091] Tenant + user-management data hooks.
//
// Backs:
//   apps/web/src/screens/Tenants.tsx       — list + provision (platform_admin)
//   apps/web/src/screens/TenantUsers.tsx   — list + invite + role + remove
//
// All requests go through the admin API at /admin. Role gates are
// enforced server-side; the screens also use <RequireRole> as
// defense-in-depth so a route-guard regression doesn't expose a
// surface the server still happens to permit.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "./api";

// ────────────────────────────────────────────────────────────────────────
// Types — wire shapes the admin API returns. ISO strings rather than
// Dates because JSON has no Date.
// ────────────────────────────────────────────────────────────────────────

// Region + timeZone are S-095g — backing for the Settings General
// section dropdowns. Region is set at provisioning and read-only here;
// time zone is freely editable via PATCH /tenants/:id/time-zone.
export type TenantRegion = "us-west-2" | "us-east-1" | "eu-west-1";
export type TenantTimeZone =
  | "America/Los_Angeles"
  | "America/New_York"
  | "UTC";

export type TenantRow = {
  readonly id: string;
  readonly studioId: string;
  readonly tenantNamespace: string;
  readonly deploymentMode: "saas" | "hybrid" | "on_prem";
  readonly accessTier: "basic" | "professional" | "enterprise";
  readonly region: TenantRegion;
  readonly timeZone: TenantTimeZone;
  readonly provisionedBy: string;
  readonly createdAt: string;
  readonly initialTenantAdminUid: string;
};

export type TenantsListResponse = {
  tenants: TenantRow[];
};

export type ProvisionTenantInput = {
  studioName: string;
  deploymentMode: TenantRow["deploymentMode"];
  accessTier: TenantRow["accessTier"];
  initialTenantAdminEmail: string;
};

// S-295 — mirrors @csai/core's InviteSendStatus (kept local like the other
// wire types in this file so the data layer carries no core import).
export type InviteSendStatus = "emailed" | "email_failed" | "link_only";

export type TenantUserRow = {
  readonly uid: string;
  readonly tenantId: string;
  readonly role: "platform_admin" | "tenant_admin" | "tenant_user";
  readonly accessTier: TenantRow["accessTier"];
  readonly email: string;
  readonly displayName: string | null;
  readonly createdAt: string;
  readonly deactivatedAt: string | null;
  // S-295 — last invite (re)send metadata for the inline status badge.
  readonly lastInviteSentAt: string | null;
  readonly lastInviteStatus: InviteSendStatus | null;
  // S-316 — the tenant custom role assigned to this member (machine name), or
  // null when they run on their built-in role default. Drives the effective-
  // role label + the role-select's current value.
  readonly assignedRoleName?: string | null;
};

// S-091a — wire shape of POST /tenants/:id/users. The user record is
// the full TenantUserRow; the link fields are present once on creation
// and never returned on subsequent reads. Both are null when the admin
// API is running without an IdentityProviderPort configured (dev
// fallback when firebase-admin/auth isn't wired).
export type InviteTenantUserResponse = TenantUserRow & {
  readonly inviteLink: string | null;
  readonly inviteLinkExpiresAt: string | null;
  // S-295 — best-effort invite-email outcome. null when the admin API has
  // no mailer wired (the copy-the-link fallback still applies); otherwise
  // tells the UI whether the email was delivered.
  readonly inviteEmail: { readonly delivered: boolean; readonly error: string | null } | null;
};

export type TenantUsersListResponse = {
  users: TenantUserRow[];
};

export type InviteTenantUserInput = {
  email: string;
  role: "tenant_admin" | "tenant_user";
  // S-316 — when set, the invitee is granted this tenant custom role (the
  // backend floors them to tenant_user and bakes in the role's capabilities).
  customRoleName?: string;
};

export type UpdateTenantUserRoleInput = {
  role: TenantUserRow["role"];
  // S-316 — when set, switch the member to this tenant custom role instead of
  // a built-in one.
  customRoleName?: string;
};

// ────────────────────────────────────────────────────────────────────────
// Query keys
// ────────────────────────────────────────────────────────────────────────

export const TENANTS_KEY = ["admin", "tenants"] as const;
export const TENANT_USERS_KEY = ["admin", "tenant-users"] as const;

// ────────────────────────────────────────────────────────────────────────
// Tenants
// ────────────────────────────────────────────────────────────────────────

export function useTenantsList(
  options: { enabled?: boolean } = {}
): UseQueryResult<TenantsListResponse, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...TENANTS_KEY, "list"],
    queryFn: () => api<TenantsListResponse>("/tenants"),
    enabled: options.enabled ?? true,
  });
}

/**
 * [S-095a] Single-tenant read for the Settings General section.
 * Any tenant member or platform_admin can read; the server returns
 * the full Tenant shape including accessTier + deploymentMode +
 * createdAt that the General row binders need.
 */
export function useTenantDetail(
  tenantId: string
): UseQueryResult<TenantRow, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...TENANTS_KEY, "detail", tenantId],
    queryFn: () =>
      api<TenantRow>(`/tenants/${encodeURIComponent(tenantId)}`),
    enabled: tenantId.length > 0,
  });
}

export function useProvisionTenant(): UseMutationResult<
  TenantRow,
  Error,
  ProvisionTenantInput
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api<TenantRow>("/tenants", {
        method: "POST",
        body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANTS_KEY });
    },
  });
}

// ────────────────────────────────────────────────────────────────────────
// Tenant users
// ────────────────────────────────────────────────────────────────────────

export function useTenantUsersList(
  tenantId: string
): UseQueryResult<TenantUsersListResponse, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...TENANT_USERS_KEY, "list", tenantId],
    queryFn: () =>
      api<TenantUsersListResponse>(
        `/tenants/${encodeURIComponent(tenantId)}/users`
      ),
    enabled: tenantId.length > 0,
  });
}

export function useInviteTenantUser(
  tenantId: string
): UseMutationResult<InviteTenantUserResponse, Error, InviteTenantUserInput> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api<InviteTenantUserResponse>(
        `/tenants/${encodeURIComponent(tenantId)}/users`,
        { method: "POST", body: input }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...TENANT_USERS_KEY, "list", tenantId],
      });
    },
  });
}

/**
 * S-295 — re-mint a fresh first-time sign-in link for an existing user and
 * (re)send the invite email. Same response shape as the initial invite, so
 * the success dialog (link + email status) is reused. Invalidates the user
 * list so the row's "last invited" badge refreshes.
 */
export function useResendTenantInvite(
  tenantId: string
): UseMutationResult<InviteTenantUserResponse, Error, { uid: string }> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uid }) =>
      api<InviteTenantUserResponse>(
        `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(uid)}/resend-invite`,
        { method: "POST" }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...TENANT_USERS_KEY, "list", tenantId],
      });
    },
  });
}

/**
 * [S-095g] Update the tenant display time zone from the Settings
 * General section. Tenant_admin of the tenant or platform_admin only;
 * the server enforces, the UI route guard mirrors.
 */
export function useUpdateTenantTimeZone(
  tenantId: string
): UseMutationResult<TenantRow, Error, TenantTimeZone> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (timeZone) =>
      api<TenantRow>(
        `/tenants/${encodeURIComponent(tenantId)}/time-zone`,
        { method: "PATCH", body: { timeZone } }
      ),
    onSuccess: (next) => {
      qc.setQueryData([...TENANTS_KEY, "detail", tenantId], next);
    },
  });
}

export function useUpdateTenantUserRole(
  tenantId: string
): UseMutationResult<
  void,
  Error,
  // S-316 — `customRoleName` (when present) switches the member to a tenant
  // custom role; otherwise `role` selects a built-in one.
  { uid: string; role: TenantUserRow["role"]; customRoleName?: string }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uid, role, customRoleName }) =>
      api<void>(
        `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(uid)}`,
        {
          method: "PATCH",
          body:
            customRoleName !== undefined
              ? { role, customRoleName }
              : { role },
        }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...TENANT_USERS_KEY, "list", tenantId],
      });
    },
  });
}

export function useRemoveTenantUser(
  tenantId: string
): UseMutationResult<void, Error, string> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uid) =>
      api<void>(
        `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(uid)}`,
        { method: "DELETE" }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...TENANT_USERS_KEY, "list", tenantId],
      });
    },
  });
}
